import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useRTWList, useRTWCreate, useRTWUpdate, useRTWDelete, useCCUsers } from "../../../../api-hooks/compliance";

const STATUS_COLOR: Record<string, string> = { pass: "green", fail: "red", pending: "orange" };
const DOC_TYPES = ["passport", "brp", "share_code", "birth_cert_ni", "eu_settlement"];

export default function RTWTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);

  const { data: res, isLoading } = useRTWList(cid, { page, limit: 20 });
  const { data: users = [] } = useCCUsers(cid);
  const rows = (res as any)?.data ?? [];
  const total = (res as any)?.total ?? 0;

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useRTWCreate(cid, cb("RTW check added."));
  const update = useRTWUpdate(cid, cb("Updated."));
  const remove = useRTWDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openEdit(r?: any) {
    setModal({ open: true, rec: r ?? null });
    form.setFieldsValue(r ? { ...r, check_date: r.check_date ? dayjs(r.check_date) : null, expiry_date: r.expiry_date ? dayjs(r.expiry_date) : null, follow_up_date: r.follow_up_date ? dayjs(r.follow_up_date) : null } : { status: "pending" });
  }

  const columns = [
    { title: "Worker", dataIndex: "user_name", key: "u" },
    { title: "Doc Type", dataIndex: "document_type", key: "dt", render: (v: string) => v?.replace(/_/g, " ").toUpperCase() },
    { title: "Doc #", dataIndex: "document_number", key: "dn", render: (v: string) => v || "—" },
    { title: "Check Date", dataIndex: "check_date", key: "cd", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.toUpperCase()}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">Right to Work Checks</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openEdit()} style={{ background: "#4c1d95" }}>Add Check</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={columns} loading={isLoading} size="small" pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit RTW Check" : "New RTW Check"} width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, check_date: vals.check_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD"), follow_up_date: vals.follow_up_date?.format("YYYY-MM-DD") };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="user_id" label="Worker" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
            <Form.Item name="document_type" label="Document Type" rules={[{ required: true }]}><Select options={DOC_TYPES.map(d => ({ value: d, label: d.replace(/_/g, " ").toUpperCase() }))} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="check_date" label="Check Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="document_number" label="Document #"><Input /></Form.Item>
            <Form.Item name="share_code" label="Share Code"><Input /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="status" label="Status" rules={[{ required: true }]}><Select options={[{ value: "pending", label: "Pending" }, { value: "pass", label: "Pass" }, { value: "fail", label: "Fail" }]} /></Form.Item>
            <Form.Item name="follow_up_date" label="Follow-up Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
